import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Badge, Modal, Button } from "react-bootstrap";
import {
  FiTrash2,
  FiDownload,
  FiSend,
  FiCreditCard,
  FiDollarSign,
  FiClock,
  FiEdit2,
  FiEye,
  FiPlus,
  FiX,
  FiCalendar,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import ConfirmModal from "../components/ConfirmModal";
import api from "../services/api";

const Transactions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);

  // Initialize filter and page from URL params so state is restored on back-navigation
  const [filter, setFilter] = useState(() => ({
    status: searchParams.get("status") || "",
    payment_method: searchParams.get("payment_method") || "",
    date_from: searchParams.get("date_from") || "",
    date_to: searchParams.get("date_to") || "",
    date_field: searchParams.get("date_field") || "created_at",
    search: searchParams.get("search") || "",
    laundry_type: searchParams.get("laundry_type") || "",
  }));
  const [showFilters, setShowFilters] = useState(() => {
    // Auto-buka panel jika ada filter aktif dari URL
    return !!(
      searchParams.get("status") ||
      searchParams.get("payment_method") ||
      searchParams.get("date_from") ||
      searchParams.get("date_to") ||
      searchParams.get("laundry_type")
    );
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(() =>
    parseInt(searchParams.get("page") || "1", 10),
  );
  const pageRef = useRef(parseInt(searchParams.get("page") || "1", 10));
  const [totalCount, setTotalCount] = useState(0);
  const [sort, setSort] = useState(() => ({
    by: searchParams.get("sort_by") || "created_at",
    dir: searchParams.get("sort_dir") || "desc",
  }));
  const sortRef = useRef({
    by: searchParams.get("sort_by") || "created_at",
    dir: searchParams.get("sort_dir") || "desc",
  });
  const PAGE_SIZE = 20;
  const isFirstRender = useRef(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingTx, setPayingTx] = useState(null);
  const [payMethod, setPayMethod] = useState("cash");
  const [paySplitRows, setPaySplitRows] = useState([
    { method: "cash", amount: "" },
    { method: "qris", amount: "" },
  ]);
  const [payAmount, setPayAmount] = useState("");
  const [specialPaymentMethods, setSpecialPaymentMethods] = useState([]);
  const [selectedSpecialPayment, setSelectedSpecialPayment] = useState(null);
  const [payDiscount, setPayDiscount] = useState("");
  const [payDiscountType, setPayDiscountType] = useState("flat");
  const [payVoucherCode, setPayVoucherCode] = useState("");

  // DP modal state
  const [showDpModal, setShowDpModal] = useState(false);
  const [dpTx, setDpTx] = useState(null);
  const [dpAmount, setDpAmount] = useState("");
  const [dpMethod, setDpMethod] = useState("cash");
  const [payValidatedVoucher, setPayValidatedVoucher] = useState(null);
  const [payNotes, setPayNotes] = useState("");
  const [showWAConfirm, setShowWAConfirm] = useState(false);
  const [doneTx, setDoneTx] = useState(null);
  const [waSending, setWaSending] = useState(false);

  // WA invoice confirmation after payment
  const [showWAInvoiceConfirm, setShowWAInvoiceConfirm] = useState(false);
  const [paidTxForWA, setPaidTxForWA] = useState(null);
  const [waInvoiceSending, setWaInvoiceSending] = useState(false);

  // Packing modal
  const [showPackingModal, setShowPackingModal] = useState(false);
  const [packingTx, setPackingTx] = useState(null);
  const [packingCount, setPackingCount] = useState("");
  const [packingTargetStatus, setPackingTargetStatus] = useState("");

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [editItems, setEditItems] = useState([]); // unified list
  const [editItemType, setEditItemType] = useState("service");
  const [editItemId, setEditItemId] = useState("");
  const [editItemQty, setEditItemQty] = useState(1);
  const [editServices, setEditServices] = useState([]);
  const [editProducts, setEditProducts] = useState([]);
  const [editVehicleId, setEditVehicleId] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editPaymentMethod, setEditPaymentMethod] = useState("");
  const [editPaymentAmount, setEditPaymentAmount] = useState("");
  const [editPaidAt, setEditPaidAt] = useState("");
  const [editShiftId, setEditShiftId] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editWeightKg, setEditWeightKg] = useState("");
  const [editTotalPcs, setEditTotalPcs] = useState("");
  const [editClothingNotes, setEditClothingNotes] = useState("");
  const [editDiscountAmount, setEditDiscountAmount] = useState("");
  const [editDpPaymentMethod, setEditDpPaymentMethod] = useState("cash");
  const [editDpAmount, setEditDpAmount] = useState("");
  const [editHasDp, setEditHasDp] = useState(false);
  const [editShifts, setEditShifts] = useState([]);
  const [editTab, setEditTab] = useState("umum");

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmLabel: "Ya",
    confirmVariant: "danger",
  });

  // Cancel (batal) modal state
  const [cancelModal, setCancelModal] = useState({
    show: false,
    tx: null,
    reason: "",
    loading: false,
  });

  const openConfirm = (
    title,
    message,
    onConfirm,
    confirmLabel = "Ya, Hapus",
    confirmVariant = "danger",
  ) => {
    setConfirmModal({
      show: true,
      title,
      message,
      onConfirm,
      confirmLabel,
      confirmVariant,
    });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, show: false, onConfirm: null }));
  };

  // Load payment methods once on mount
  useEffect(() => {
    api
      .get("/payment-methods/active")
      .then((r) =>
        setSpecialPaymentMethods(Array.isArray(r.data) ? r.data : []),
      )
      .catch(() => {});
  }, []);

  // Sync filter + page to URL (replace so back-button skips these micro-changes)
  useEffect(() => {
    const p = new URLSearchParams();
    if (filter.status) p.set("status", filter.status);
    if (filter.payment_method) p.set("payment_method", filter.payment_method);
    if (filter.date_from) p.set("date_from", filter.date_from);
    if (filter.date_to) p.set("date_to", filter.date_to);
    if (filter.date_field && filter.date_field !== "created_at")
      p.set("date_field", filter.date_field);
    if (filter.search) p.set("search", filter.search);
    if (filter.laundry_type) p.set("laundry_type", filter.laundry_type);
    if (pageRef.current > 1) p.set("page", pageRef.current.toString());
    if (sortRef.current.by !== "created_at")
      p.set("sort_by", sortRef.current.by);
    if (sortRef.current.dir !== "desc") p.set("sort_dir", sortRef.current.dir);
    setSearchParams(p, { replace: true });
  }, [filter, page, sort]);

  // Auto-search: on first render restore page from URL, otherwise reset to page 1
  const searchDebounce = useRef(null);
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Load with URL-restored page (no reset)
      searchDebounce.current = setTimeout(
        () => loadTransactions(pageRef.current),
        0,
      );
      return () => clearTimeout(searchDebounce.current);
    }
    searchDebounce.current = setTimeout(() => {
      pageRef.current = 1;
      setPage(1);
      loadTransactions(1);
    }, 300);
    return () => clearTimeout(searchDebounce.current);
  }, [filter]);

  const loadTransactions = async (currentPage) => {
    const p = currentPage !== undefined ? currentPage : pageRef.current;
    try {
      const params = { page: p, limit: PAGE_SIZE };
      if (filter.status) params.status = filter.status;
      if (filter.payment_method) params.payment_method = filter.payment_method;
      if (filter.date_from) params.date_from = filter.date_from;
      if (filter.date_to) params.date_to = filter.date_to;
      if (filter.date_field) params.date_field = filter.date_field;
      if (filter.search) params.search = filter.search;
      if (filter.laundry_type) params.laundry_type = filter.laundry_type;
      params.sort_by = sortRef.current.by;
      params.sort_dir = sortRef.current.dir;
      const res = await api.get("/transactions", { params });
      setTransactions(res.data.transactions || []);
      setTotalCount(res.data.total || 0);
    } catch (error) {
      toast.error("Gagal memuat transaksi");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    const maxPage = Math.ceil(totalCount / PAGE_SIZE);
    if (newPage < 1 || newPage > maxPage) return;
    pageRef.current = newPage;
    setPage(newPage);
    loadTransactions(newPage);
  };

  const handleSort = (col) => {
    const newSort = {
      by: col,
      dir:
        sortRef.current.by === col && sortRef.current.dir === "desc"
          ? "asc"
          : "desc",
    };
    sortRef.current = newSort;
    setSort(newSort);
    pageRef.current = 1;
    setPage(1);
    loadTransactions(1);
  };

  const SortIcon = ({ col }) => {
    if (sort.by !== col)
      return (
        <span className="ms-1 opacity-25">
          <FiChevronDown size={12} />
        </span>
      );
    return sort.dir === "asc" ? (
      <FiChevronUp size={12} className="ms-1 text-primary" />
    ) : (
      <FiChevronDown size={12} className="ms-1 text-primary" />
    );
  };

  const clearFilter = (key) => {
    setFilter((prev) => ({ ...prev, [key]: "" }));
  };

  const openPackingModal = (tx, targetStatus) => {
    setPackingTx(tx);
    setPackingTargetStatus(targetStatus);
    setPackingCount("");
    setShowPackingModal(true);
  };

  const submitPacking = async () => {
    const count = parseInt(packingCount, 10);
    if (!count || count < 1) {
      toast.error("Masukkan jumlah packingan yang valid");
      return;
    }
    setShowPackingModal(false);
    try {
      await api.put(`/transactions/${packingTx.id}/status`, {
        status: packingTargetStatus,
        packing_count: count,
      });
      const labels = {
        selesai_setrika: "Selesai Setrika",
        siap_ambil: "Siap Diambil",
      };
      toast.success(
        `Status diupdate ke ${labels[packingTargetStatus] || packingTargetStatus}`,
      );
      if (packingTargetStatus === "siap_ambil") {
        // reload first, then show WA
        await loadTransactions();
        const updated =
          transactions.find((t) => t.id === packingTx.id) || packingTx;
        setDoneTx({
          ...updated,
          id: packingTx.id,
          invoice_number: packingTx.invoice_number,
          customer_phone: packingTx.customer_phone,
          customer_name: packingTx.customer_name,
        });
        setShowWAConfirm(true);
      } else {
        loadTransactions();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal update status");
    } finally {
      setPackingTx(null);
      setPackingCount("");
    }
  };

  const doUpdateStatus = async (id, status) => {
    try {
      await api.put(`/transactions/${id}/status`, { status });
      const labels = {
        diterima: "Diterima",
        dicuci: "Dicuci",
        disetrika: "Disetrika",
        siap_ambil: "Siap Diambil",
        diambil: "Diambil",
        cancelled: "Dibatalkan",
      };
      toast.success(`Status diupdate ke ${labels[status] || status}`);
      if (status === "siap_ambil") {
        const tx = transactions.find((t) => t.id === id);
        if (tx) {
          setDoneTx(tx);
          setShowWAConfirm(true);
        }
      }
      loadTransactions();
    } catch (error) {
      toast.error("Gagal update status");
    }
  };

  const updateStatus = (tx, status) => {
    const labels = {
      dicuci: "Dicuci",
      disetrika: "Disetrika",
      siap_ambil: "Siap Diambil",
      diambil: "Diambil",
    };
    const label = labels[status] || status;
    openConfirm(
      `Ubah Status ke ${label}`,
      `Yakin ubah status transaksi ${tx.invoice_number} ke "${label}"?`,
      () => {
        closeConfirm();
        doUpdateStatus(tx.id, status);
      },
      `Ya, ${label}`,
      "primary",
    );
  };

  // Always use whatsapp-completion endpoint, since modal only appears after marking as done
  const sendWA = async () => {
    if (!doneTx) return;
    setWaSending(true);
    try {
      // Always use whatsapp-completion endpoint
      const preview = await api.get(
        `/transactions/${doneTx.id}/whatsapp-completion`,
      );
      let phone = (preview.data.phone || "").replace(/[^0-9]/g, "");
      // Format nomor ke 62xxx
      if (phone.startsWith("0")) phone = "62" + phone.slice(1);
      else if (phone.startsWith("8")) phone = "62" + phone;
      const message = preview.data.message || "";
      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.open(waUrl, "_blank");
      toast.success("WhatsApp Web dibuka!");
    } catch (error) {
      toast.error("Gagal membuka WhatsApp Web");
    } finally {
      setWaSending(false);
      setShowWAConfirm(false);
      setDoneTx(null);
    }
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
  const fmtDt = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    const date = dt.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const h = String(dt.getHours()).padStart(2, "0");
    const m = String(dt.getMinutes()).padStart(2, "0");
    return `${date}, ${h}:${m}`;
  };

  const statusBadge = (status) => {
    const map = {
      diterima: { bg: "warning", label: "Diterima" },
      dicuci: { bg: "info", label: "Dicuci" },
      disetrika: { bg: "primary", label: "Disetrika" },
      selesai_setrika: { bg: "dark", label: "Selesai Setrika" },
      siap_ambil: { bg: "success", label: "Siap Ambil" },
      diambil: { bg: "secondary", label: "Diambil" },
      cancelled: { bg: "danger", label: "Dibatalkan" },
    };
    const s = map[status] || { bg: "secondary", label: status };
    return <Badge bg={s.bg}>{s.label}</Badge>;
  };

  const handleDelete = (tx) => {
    setCancelModal({ show: true, tx, reason: "", loading: false });
  };

  const handleCancelConfirm = async () => {
    if (!cancelModal.reason.trim()) {
      toast.error("Alasan pembatalan wajib diisi");
      return;
    }
    setCancelModal((m) => ({ ...m, loading: true }));
    try {
      await api.delete(`/transactions/${cancelModal.tx.id}`, {
        data: { cancel_reason: cancelModal.reason.trim() },
      });
      toast.success("Transaksi berhasil dibatalkan");
      setCancelModal({ show: false, tx: null, reason: "", loading: false });
      loadTransactions();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Gagal membatalkan transaksi",
      );
      setCancelModal((m) => ({ ...m, loading: false }));
    }
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append("status", filter.status);
      if (filter.payment_method)
        params.append("payment_method", filter.payment_method);
      if (filter.date_from) params.append("date_from", filter.date_from);
      if (filter.date_to) params.append("date_to", filter.date_to);
      if (filter.date_field) params.append("date_field", filter.date_field);
      const res = await api.get(
        `/transactions/export/excel?${params.toString()}`,
        {
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `transaksi-${filter.date_from || "all"}-${filter.date_to || "all"}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export berhasil");
    } catch (error) {
      toast.error("Gagal export Excel");
    }
  };

  const paymentIcons = {
    cash: <FiDollarSign />,
    debit: <FiCreditCard />,
    credit: <FiCreditCard />,
    qris: <span style={{ fontSize: "0.8em" }}>QR</span>,
    transfer: <span style={{ fontSize: "0.8em" }}>TF</span>,
  };

  const openPayModal = async (tx) => {
    // Non-admin/manager must have an active shift for today
    if (!["admin", "manager"].includes(user?.role)) {
      try {
        const res = await api.get("/shifts/current");
        if (!res.data.active) {
          toast.error("Buka shift terlebih dahulu sebelum menerima pembayaran");
          navigate("/shifts");
          return;
        }
        if (!res.data.is_today) {
          toast.error(
            "Shift hari sebelumnya belum ditutup. Tutup shift tersebut terlebih dahulu.",
          );
          navigate("/shifts");
          return;
        }
      } catch {
        toast.error("Gagal memeriksa status shift");
        return;
      }
    }
    setPayingTx(tx);
    setPayMethod("cash");
    setPayAmount("");
    setSelectedSpecialPayment(null);
    setPayDiscount("");
    setPayDiscountType("flat");
    setPayVoucherCode("");
    setPayValidatedVoucher(null);
    setPayNotes("");
    setShowPayModal(true);
  };

  const validatePayVoucher = async () => {
    if (!payVoucherCode) return;
    try {
      const res = await api.post("/promos/vouchers/validate", {
        code: payVoucherCode,
      });
      if (res.data.valid) {
        setPayValidatedVoucher(res.data.voucher);
        toast.success("Voucher valid!");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Voucher tidak valid");
      setPayValidatedVoucher(null);
    }
  };

  // For partial transactions, the effective total to pay is the remaining (total - dp)
  const payRawTotal =
    payingTx?.payment_method === "partial"
      ? Math.max(
          0,
          Number(payingTx?.total_amount || 0) -
            Number(payingTx?.dp_amount || 0),
        )
      : Number(payingTx?.total_amount || 0);
  const payManualDiscountAmt =
    payDiscountType === "percentage"
      ? payRawTotal * (Number(payDiscount) / 100)
      : Number(payDiscount || 0);
  const payVoucherDiscountAmt = payValidatedVoucher
    ? payValidatedVoucher.discount_type === "percentage"
      ? payRawTotal * (payValidatedVoucher.discount_value / 100)
      : Number(payValidatedVoucher.discount_value)
    : 0;
  const payFinalTotal =
    selectedSpecialPayment && selectedSpecialPayment.override_price != null
      ? Number(selectedSpecialPayment.override_price)
      : Math.max(0, payRawTotal - payManualDiscountAmt - payVoucherDiscountAmt);

  const handlePay = async () => {
    const effectiveTotal = payFinalTotal;
    if (
      !selectedSpecialPayment &&
      payMethod === "cash" &&
      Number(payAmount) < effectiveTotal
    ) {
      toast.error("Pembayaran kurang");
      return;
    }
    if (!selectedSpecialPayment && payMethod === "split") {
      const splitTotal = paySplitRows.reduce(
        (s, r) => s + Number(r.amount || 0),
        0,
      );
      if (Math.abs(splitTotal - effectiveTotal) > 1) {
        toast.error(
          `Total split ${fmt(splitTotal)} ≠ total tagihan ${fmt(effectiveTotal)}`,
        );
        return;
      }
    }
    try {
      const splitPayloads =
        !selectedSpecialPayment && payMethod === "split"
          ? paySplitRows.map((r) => ({
              method: r.method,
              amount: Number(r.amount),
            }))
          : undefined;
      const res = await api.put(`/transactions/${payingTx.id}/pay`, {
        payment_method: selectedSpecialPayment ? "special" : payMethod,
        payment_amount:
          payMethod === "split" && !selectedSpecialPayment
            ? paySplitRows.reduce((s, r) => s + Number(r.amount || 0), 0)
            : payMethod === "cash" && !selectedSpecialPayment
              ? Number(payAmount)
              : effectiveTotal,
        split_payments: splitPayloads,
        special_payment_method_id: selectedSpecialPayment?.id || null,
        special_payment_override_price:
          selectedSpecialPayment &&
          selectedSpecialPayment.override_price != null
            ? Number(selectedSpecialPayment.override_price)
            : null,
        discount_amount: selectedSpecialPayment ? 0 : payManualDiscountAmt,
        voucher_id: selectedSpecialPayment
          ? null
          : payValidatedVoucher?.id || null,
        notes: payNotes || null,
      });
      toast.success(
        `Pembayaran berhasil!${res.data.change_amount > 0 ? ` Kembalian: ${fmt(res.data.change_amount)}` : ""}`,
      );
      setShowPayModal(false);
      loadTransactions();
      // Show WA invoice confirmation if customer has phone
      if (payingTx.customer_phone) {
        setPaidTxForWA(payingTx);
        setShowWAInvoiceConfirm(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal membayar");
    }
  };

  const openDpModal = async (tx) => {
    if (!["admin", "manager"].includes(user?.role)) {
      try {
        const res = await api.get("/shifts/current");
        if (!res.data.active) {
          toast.error("Buka shift terlebih dahulu sebelum menerima pembayaran");
          navigate("/shifts");
          return;
        }
        if (!res.data.is_today) {
          toast.error("Shift hari sebelumnya belum ditutup.");
          navigate("/shifts");
          return;
        }
      } catch {
        toast.error("Gagal memeriksa status shift");
        return;
      }
    }
    setDpTx(tx);
    setDpAmount("");
    setDpMethod("cash");
    setShowDpModal(true);
  };

  const handleDp = async () => {
    const dp = Number(dpAmount);
    if (!dp || dp <= 0) {
      toast.error("Masukkan jumlah DP");
      return;
    }
    if (dp >= Number(dpTx?.total_amount)) {
      toast.error("DP harus kurang dari total. Gunakan Bayar Lunas.");
      return;
    }
    try {
      await api.put(`/transactions/${dpTx.id}/pay-dp`, {
        dp_amount: dp,
        dp_payment_method: dpMethod,
      });
      toast.success("DP berhasil dicatat");
      setShowDpModal(false);
      loadTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal mencatat DP");
    }
  };

  const toDatetimeLocal = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const openEditModal = async (tx) => {
    setEditingTx(tx);
    setEditDate(toDatetimeLocal(tx.created_at));
    setEditItemType("service");
    setEditItemId("");
    setEditItemQty(1);
    setEditTab("umum");
    // Admin-only fields
    setEditStatus(tx.status || "diterima");
    setEditPaymentMethod(tx.payment_method || "unpaid");
    setEditPaymentAmount(tx.payment_amount || "");
    setEditPaidAt(toDatetimeLocal(tx.paid_at));
    setEditShiftId(tx.shift_id || "");
    setEditNotes(tx.notes || "");
    setEditWeightKg(tx.weight_kg ?? "");
    setEditTotalPcs(tx.total_pcs ?? "");
    setEditClothingNotes(tx.clothing_notes || "");
    setEditDiscountAmount(tx.discount_amount || "");
    setEditDpPaymentMethod(tx.dp_payment_method || "cash");
    setEditDpAmount(tx.dp_amount || "");
    setEditHasDp(Number(tx.dp_amount) > 0);
    const [sRes, pRes, txRes, shiftRes] = await Promise.all([
      editServices.length === 0
        ? api.get("/services").catch(() => ({ data: [] }))
        : Promise.resolve({ data: editServices }),
      editProducts.length === 0
        ? api.get("/products").catch(() => ({ data: [] }))
        : Promise.resolve({ data: editProducts }),
      api
        .get(`/transactions/${tx.id}`)
        .catch(() => ({ data: { details: [] } })),
      editShifts.length === 0
        ? api.get("/shifts").catch(() => ({ data: [] }))
        : Promise.resolve({ data: editShifts }),
    ]);
    const svcs =
      editServices.length === 0
        ? Array.isArray(sRes.data)
          ? sRes.data.filter((s) => s.is_active)
          : []
        : editServices;
    const prods =
      editProducts.length === 0
        ? Array.isArray(pRes.data)
          ? pRes.data.filter((p) => p.is_active)
          : []
        : editProducts;
    if (editServices.length === 0) setEditServices(svcs);
    if (editProducts.length === 0) setEditProducts(prods);
    if (editShifts.length === 0 && Array.isArray(shiftRes.data))
      setEditShifts(shiftRes.data);
    // Map existing details to unified list
    const details = Array.isArray(txRes.data?.details)
      ? txRes.data.details
      : [];
    setEditItems(
      details.map((d) => ({
        detailId: d.id,
        type: d.item_type,
        id: d.service_id || d.product_id,
        name: d.item_name,
        price: Number(d.unit_price),
        quantity: d.quantity,
        isNew: false,
      })),
    );
    setShowEditModal(true);
  };

  const addEditItem = () => {
    if (!editItemId) return;
    const list = editItemType === "service" ? editServices : editProducts;
    const found = list.find((i) => i.id === Number(editItemId));
    if (!found) return;
    setEditItems([
      ...editItems,
      {
        detailId: null,
        type: editItemType,
        id: found.id,
        name: found.name,
        price: editItemType === "service" ? found.price : found.sell_price,
        quantity: Number(editItemQty) || 1,
        isNew: true,
      },
    ]);
    setEditItemId("");
    setEditItemQty(1);
  };

  const handleEditSave = async () => {
    try {
      const payload = {
        created_at: editDate ? new Date(editDate).toISOString() : undefined,
        items: editItems.map(({ detailId, type, id, quantity, price }) => ({
          detailId: detailId || undefined,
          type,
          id,
          quantity,
          unit_price: price,
        })),
      };
      if (user?.role === "admin") {
        payload.status = editStatus;
        payload.payment_method = editPaymentMethod;
        payload.payment_amount =
          editPaymentAmount !== "" ? Number(editPaymentAmount) : undefined;
        payload.paid_at = editPaidAt
          ? new Date(editPaidAt).toISOString()
          : null;
        payload.shift_id = editShiftId !== "" ? Number(editShiftId) : null;
        payload.notes = editNotes;
        payload.weight_kg = editWeightKg !== "" ? Number(editWeightKg) : null;
        payload.total_pcs = editTotalPcs !== "" ? Number(editTotalPcs) : null;
        payload.clothing_notes = editClothingNotes;
        payload.discount_amount =
          editDiscountAmount !== "" ? Number(editDiscountAmount) : undefined;
        if (editHasDp) {
          payload.dp_amount =
            editDpAmount !== "" ? Number(editDpAmount) : undefined;
          payload.dp_payment_method = editDpPaymentMethod;
        } else if (Number(editingTx?.dp_amount) > 0) {
          payload.dp_amount = 0;
          payload.dp_payment_method = null;
        }
      }
      await api.put(`/transactions/${editingTx.id}/edit`, payload);
      toast.success("Transaksi berhasil diupdate");
      setShowEditModal(false);
      loadTransactions();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Gagal mengupdate transaksi",
      );
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Daftar Transaksi</h4>
        <button className="btn btn-success btn-sm" onClick={exportExcel}>
          <FiDownload className="me-1" /> Excel
        </button>
      </div>

      {/* Search + Filter toggle — 1 baris */}
      <div className="card p-3 mb-3">
        <div className="d-flex gap-2">
          {/* Search */}
          <div style={{ position: "relative", flex: 1 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Cari invoice, nota, customer..."
              value={filter.search}
              style={filter.search ? { paddingRight: "2rem" } : {}}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            />
            {filter.search && (
              <button
                className="btn p-0 border-0 bg-transparent text-secondary"
                style={{
                  position: "absolute",
                  right: "0.5rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  lineHeight: 1,
                  zIndex: 2,
                }}
                onClick={() => clearFilter("search")}
                tabIndex={-1}
              >
                <FiX size={14} />
              </button>
            )}
          </div>
          {/* Filter button */}
          {(() => {
            const activeCount = [
              filter.status,
              filter.laundry_type,
              filter.payment_method,
              filter.date_from || filter.date_to,
            ].filter(Boolean).length;
            return (
              <button
                className={`btn btn-sm d-flex align-items-center gap-1 ${
                  activeCount > 0 ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FiFilter size={14} />
                Filter
                {activeCount > 0 && (
                  <span
                    className="badge bg-white text-primary rounded-pill"
                    style={{ fontSize: "0.7rem", lineHeight: 1.4 }}
                  >
                    {activeCount}
                  </span>
                )}
                <FiChevronDown
                  size={13}
                  style={{
                    transition: "transform 0.2s",
                    transform: showFilters ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </button>
            );
          })()}
        </div>

        {/* Panel filter — collapsible */}
        {showFilters && (
          <div className="mt-3 pt-3 border-top">
            <div className="d-flex align-items-end gap-2 flex-wrap">
              {/* Periode — PERTAMA */}
              <div style={{ flex: "2 1 260px", minWidth: 0 }}>
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}
                >
                  Periode
                </label>
                <div className="d-flex align-items-center gap-1">
                  <div
                    className="btn-group btn-group-sm flex-shrink-0"
                    role="group"
                  >
                    <button
                      type="button"
                      className={`btn ${filter.date_field === "created_at" ? "btn-primary" : "btn-outline-primary"}`}
                      style={{ whiteSpace: "nowrap" }}
                      onClick={() =>
                        setFilter({ ...filter, date_field: "created_at" })
                      }
                    >
                      Masuk
                    </button>
                    <button
                      type="button"
                      className={`btn ${filter.date_field === "paid_at" ? "btn-primary" : "btn-outline-primary"}`}
                      style={{ whiteSpace: "nowrap" }}
                      onClick={() =>
                        setFilter({ ...filter, date_field: "paid_at" })
                      }
                    >
                      Bayar
                    </button>
                  </div>
                  <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                    <FiCalendar
                      size={12}
                      style={{
                        position: "absolute",
                        left: "0.5rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "var(--text-muted)",
                        pointerEvents: "none",
                        zIndex: 2,
                      }}
                    />
                    <input
                      type="date"
                      className="form-control form-control-sm date-input-custom"
                      value={filter.date_from}
                      style={{
                        paddingLeft: "1.6rem",
                        paddingRight: filter.date_from ? "1.6rem" : "0.5rem",
                        minWidth: 0,
                      }}
                      onChange={(e) =>
                        setFilter({ ...filter, date_from: e.target.value })
                      }
                    />
                    {filter.date_from && (
                      <button
                        className="btn p-0 border-0 bg-transparent text-secondary"
                        style={{
                          position: "absolute",
                          right: "0.3rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          lineHeight: 1,
                          zIndex: 2,
                        }}
                        onClick={() => clearFilter("date_from")}
                        tabIndex={-1}
                      >
                        <FiX size={12} />
                      </button>
                    )}
                  </div>
                  <span
                    className="text-muted flex-shrink-0"
                    style={{ fontSize: "0.75rem" }}
                  >
                    —
                  </span>
                  <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                    <FiCalendar
                      size={12}
                      style={{
                        position: "absolute",
                        left: "0.5rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "var(--text-muted)",
                        pointerEvents: "none",
                        zIndex: 2,
                      }}
                    />
                    <input
                      type="date"
                      className="form-control form-control-sm date-input-custom"
                      value={filter.date_to}
                      style={{
                        paddingLeft: "1.6rem",
                        paddingRight: filter.date_to ? "1.6rem" : "0.5rem",
                        minWidth: 0,
                      }}
                      onChange={(e) =>
                        setFilter({ ...filter, date_to: e.target.value })
                      }
                    />
                    {filter.date_to && (
                      <button
                        className="btn p-0 border-0 bg-transparent text-secondary"
                        style={{
                          position: "absolute",
                          right: "0.3rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          lineHeight: 1,
                          zIndex: 2,
                        }}
                        onClick={() => clearFilter("date_to")}
                        tabIndex={-1}
                      >
                        <FiX size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {/* Status */}
              <div style={{ flex: "1 1 130px", minWidth: 0 }}>
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}
                >
                  Status
                </label>
                <div style={{ position: "relative" }}>
                  <select
                    className="form-select form-select-sm"
                    value={filter.status}
                    style={filter.status ? { paddingRight: "2.5rem" } : {}}
                    onChange={(e) =>
                      setFilter({ ...filter, status: e.target.value })
                    }
                  >
                    <option value="">Semua Status</option>
                    <option value="diterima">Diterima</option>
                    <option value="dicuci">Dicuci</option>
                    <option value="disetrika">Disetrika</option>
                    <option value="selesai_setrika">Selesai Setrika</option>
                    <option value="siap_ambil">Siap Diambil</option>
                    <option value="diambil">Diambil</option>
                    <option value="cancelled">Dibatalkan</option>
                  </select>
                  {filter.status && (
                    <button
                      className="btn p-0 border-0 bg-transparent text-secondary"
                      style={{
                        position: "absolute",
                        right: "1.8rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        lineHeight: 1,
                        zIndex: 2,
                      }}
                      onClick={() => clearFilter("status")}
                      tabIndex={-1}
                    >
                      <FiX size={13} />
                    </button>
                  )}
                </div>
              </div>
              {/* Tipe */}
              <div style={{ flex: "1 1 120px", minWidth: 0 }}>
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}
                >
                  Tipe
                </label>
                <div style={{ position: "relative" }}>
                  <select
                    className="form-select form-select-sm"
                    value={filter.laundry_type}
                    style={
                      filter.laundry_type ? { paddingRight: "2.5rem" } : {}
                    }
                    onChange={(e) =>
                      setFilter({ ...filter, laundry_type: e.target.value })
                    }
                  >
                    <option value="">Semua Tipe</option>
                    <option value="reguler">Reguler</option>
                    <option value="express">Express</option>
                    <option value="super_express">Super Express</option>
                  </select>
                  {filter.laundry_type && (
                    <button
                      className="btn p-0 border-0 bg-transparent text-secondary"
                      style={{
                        position: "absolute",
                        right: "1.8rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        lineHeight: 1,
                        zIndex: 2,
                      }}
                      onClick={() => clearFilter("laundry_type")}
                      tabIndex={-1}
                    >
                      <FiX size={13} />
                    </button>
                  )}
                </div>
              </div>
              {/* Pembayaran */}
              <div style={{ flex: "1 1 130px", minWidth: 0 }}>
                <label
                  className="form-label mb-1"
                  style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}
                >
                  Pembayaran
                </label>
                <div style={{ position: "relative" }}>
                  <select
                    className="form-select form-select-sm"
                    value={filter.payment_method}
                    style={
                      filter.payment_method ? { paddingRight: "2.5rem" } : {}
                    }
                    onChange={(e) =>
                      setFilter({ ...filter, payment_method: e.target.value })
                    }
                  >
                    <option value="">Semua</option>
                    <option value="cash">Cash</option>
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                    <option value="qris">QRIS</option>
                    <option value="transfer">Transfer</option>
                    <option value="unpaid">Belum Bayar</option>
                    <option value="partial">DP (Partial Paid)</option>
                    <option value="split">Split Payment</option>
                  </select>
                  {filter.payment_method && (
                    <button
                      className="btn p-0 border-0 bg-transparent text-secondary"
                      style={{
                        position: "absolute",
                        right: "1.8rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        lineHeight: 1,
                        zIndex: 2,
                      }}
                      onClick={() => clearFilter("payment_method")}
                      tabIndex={-1}
                    >
                      <FiX size={13} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleSort("created_at")}
                  >
                    Invoice <SortIcon col="created_at" />
                  </th>
                  <th
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleSort("customer_name")}
                  >
                    Customer <SortIcon col="customer_name" />
                  </th>
                  <th
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleSort("total_amount")}
                  >
                    Total <SortIcon col="total_amount" />
                  </th>
                  <th
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleSort("payment_method")}
                  >
                    Pembayaran <SortIcon col="payment_method" />
                  </th>
                  <th
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleSort("status")}
                  >
                    Status <SortIcon col="status" />
                  </th>
                  <th>Proses</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>
                      <div className="d-flex align-items-center gap-1 flex-wrap">
                        <span>{tx.invoice_number}</span>
                        {tx.laundry_type_label === "super_express" && (
                          <Badge
                            bg="danger"
                            style={{
                              fontSize: "0.6rem",
                              letterSpacing: "0.03em",
                            }}
                          >
                            SUPER EXPRESS
                          </Badge>
                        )}
                        {tx.laundry_type_label === "express" && (
                          <Badge
                            bg="warning"
                            text="dark"
                            style={{
                              fontSize: "0.6rem",
                              letterSpacing: "0.03em",
                            }}
                          >
                            EXPRESS
                          </Badge>
                        )}
                      </div>
                      <small className="text-muted">
                        {fmtDt(tx.created_at)}
                      </small>
                    </td>
                    <td>
                      {tx.customer_name || "-"}
                      {(tx.loyalty_progress || []).length > 0 && (
                        <div>
                          {tx.loyalty_progress.map((lp) => (
                            <small
                              key={lp.rule_id}
                              className="d-block"
                              style={{ fontSize: "0.7rem" }}
                            >
                              {lp.rule_name}:{" "}
                              <strong>
                                {lp.current_count}/{lp.required_count}
                              </strong>
                              {lp.current_count >= lp.required_count && (
                                <Badge
                                  bg="success"
                                  className="ms-1"
                                  style={{ fontSize: "0.55rem" }}
                                >
                                  REDEEM
                                </Badge>
                              )}
                            </small>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="fw-bold">{fmt(tx.total_amount)}</td>
                    <td>
                      {tx.payment_method === "unpaid" ? (
                        <Badge bg="danger">BELUM BAYAR</Badge>
                      ) : tx.payment_method === "partial" ? (
                        <div>
                          <Badge bg="warning" text="dark">
                            DP
                          </Badge>
                          <div className="small text-muted mt-1">
                            DP: {fmt(tx.dp_amount)}
                            {tx.dp_payment_method
                              ? ` (${tx.dp_payment_method.toUpperCase()})`
                              : ""}
                            <br />
                            Sisa:{" "}
                            {fmt(
                              Number(tx.total_amount) - Number(tx.dp_amount),
                            )}
                          </div>
                        </div>
                      ) : tx.payment_method === "split" ? (
                        <div>
                          <Badge bg="info">SPLIT</Badge>
                          {(() => {
                            let splits = tx.split_payments || [];
                            if (typeof splits === "string") {
                              try {
                                splits = JSON.parse(splits);
                              } catch {
                                splits = [];
                              }
                            }
                            return Array.isArray(splits)
                              ? splits.map((sp, i) => (
                                  <div key={i} className="small text-muted">
                                    {(sp.method || "").toUpperCase()}:{" "}
                                    {fmt(sp.amount)}
                                  </div>
                                ))
                              : null;
                          })()}
                        </div>
                      ) : Number(tx.dp_amount) > 0 ? (
                        <div>
                          <div>{tx.payment_method?.toUpperCase()}</div>
                          <div className="small text-muted mt-1">
                            DP
                            {tx.dp_payment_method
                              ? ` (${tx.dp_payment_method.toUpperCase()})`
                              : ""}
                            : {fmt(tx.dp_amount)}
                          </div>
                        </div>
                      ) : (
                        tx.payment_method?.toUpperCase()
                      )}
                    </td>
                    <td>{statusBadge(tx.status)}</td>
                    <td>
                      {/* Proses laundry: Bayar + status transitions */}
                      <div className="d-flex gap-1 flex-wrap">
                        {tx.payment_method === "unpaid" &&
                          tx.status !== "cancelled" &&
                          user?.role !== "penyetrika" && (
                            <>
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => openPayModal(tx)}
                              >
                                Bayar
                              </button>
                              <button
                                className="btn btn-sm btn-outline-warning"
                                onClick={() => openDpModal(tx)}
                                title="Catat uang muka (DP)"
                              >
                                DP
                              </button>
                            </>
                          )}
                        {tx.payment_method === "partial" &&
                          tx.status !== "cancelled" &&
                          user?.role !== "penyetrika" && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => openPayModal(tx)}
                            >
                              Lunasi (
                              {fmt(
                                Number(tx.total_amount) - Number(tx.dp_amount),
                              )}
                              )
                            </button>
                          )}
                        {tx.status === "diterima" &&
                          (() => {
                            const cats = (tx.service_categories || "")
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean);
                            const hasWash =
                              cats.length === 0 ||
                              cats.some(
                                (c) =>
                                  c === "Cuci Kering" || c === "Cuci + Setrika",
                              );
                            const hasIron = cats.some(
                              (c) => c === "Setrika" || c === "Cuci + Setrika",
                            );
                            if (!hasWash && hasIron) {
                              return (
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => updateStatus(tx, "disetrika")}
                                >
                                  Disetrika
                                </button>
                              );
                            }
                            if (user?.role !== "penyetrika") {
                              return (
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => updateStatus(tx, "dicuci")}
                                >
                                  Dicuci
                                </button>
                              );
                            }
                            return null;
                          })()}
                        {tx.status === "dicuci" &&
                          (() => {
                            const cats = (tx.service_categories || "")
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean);
                            const hasIron =
                              cats.length > 0 &&
                              cats.some(
                                (c) =>
                                  c === "Setrika" || c === "Cuci + Setrika",
                              );
                            if (!hasIron) {
                              return (
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() =>
                                    openPackingModal(tx, "siap_ambil")
                                  }
                                >
                                  Siap Ambil
                                </button>
                              );
                            }
                            return (
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => updateStatus(tx, "disetrika")}
                              >
                                Disetrika
                              </button>
                            );
                          })()}
                        {tx.status === "disetrika" && (
                          <button
                            className="btn btn-sm btn-dark"
                            onClick={() =>
                              openPackingModal(tx, "selesai_setrika")
                            }
                          >
                            Selesai Setrika
                          </button>
                        )}
                        {tx.status === "selesai_setrika" &&
                          user?.role !== "penyetrika" && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => updateStatus(tx, "siap_ambil")}
                            >
                              Siap Ambil
                            </button>
                          )}
                        {tx.status === "siap_ambil" &&
                          user?.role !== "penyetrika" && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => updateStatus(tx, "diambil")}
                            >
                              Diambil
                            </button>
                          )}
                      </div>
                    </td>
                    <td>
                      {/* Aksi: detail, edit, delete */}
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => navigate(`/transactions/${tx.id}`)}
                          title="Detail"
                        >
                          <FiEye size={14} />
                        </button>
                        {(user?.role === "admin" ||
                          ((tx.payment_method === "unpaid" ||
                            tx.payment_method === "partial") &&
                            tx.status !== "cancelled")) &&
                          user?.role !== "penyetrika" && (
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => openEditModal(tx)}
                              title="Edit"
                              disabled={tx.status === "cancelled"}
                            >
                              <FiEdit2 size={14} />
                            </button>
                          )}
                        {user?.role === "admin" && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(tx)}
                            title="Batalkan Transaksi"
                            disabled={tx.status === "cancelled"}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      Belum ada transaksi
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalCount > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Menampilkan {Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–
            {Math.min(page * PAGE_SIZE, totalCount)} dari {totalCount} transaksi
          </small>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page - 1)}
                >
                  ‹
                </button>
              </li>
              {(() => {
                const totalPages = Math.ceil(totalCount / PAGE_SIZE);
                const pages = [];
                for (let p = 1; p <= totalPages; p++) {
                  if (p === 1 || p === totalPages || Math.abs(p - page) <= 2) {
                    pages.push(p);
                  }
                }
                return pages.map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <li className="page-item disabled">
                        <span className="page-link">…</span>
                      </li>
                    )}
                    <li className={`page-item ${p === page ? "active" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </button>
                    </li>
                  </React.Fragment>
                ));
              })()}
              <li
                className={`page-item ${page >= Math.ceil(totalCount / PAGE_SIZE) ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page + 1)}
                >
                  ›
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Pay Modal */}
      <Modal
        show={showPayModal}
        onHide={() => setShowPayModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {payingTx?.payment_method === "partial"
              ? "Pelunasan"
              : "Pembayaran"}{" "}
            - {payingTx?.invoice_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {payingTx?.payment_method === "partial" && (
            <div className="alert alert-info py-2 small mb-3">
              DP sudah dibayar:{" "}
              <strong>{fmt(Number(payingTx?.dp_amount || 0))}</strong>
              &nbsp;|&nbsp; Sisa:{" "}
              <strong>
                {fmt(
                  Math.max(
                    0,
                    Number(payingTx?.total_amount || 0) -
                      Number(payingTx?.dp_amount || 0),
                  ),
                )}
              </strong>
            </div>
          )}
          <div className="pos-payment-total mb-3">
            <small>Total Pembayaran</small>
            <div className="pos-payment-amount">{fmt(payFinalTotal)}</div>
            {selectedSpecialPayment && (
              <small className="text-success d-block mt-1">
                Override: {selectedSpecialPayment.name}
              </small>
            )}
            {!selectedSpecialPayment &&
              (payManualDiscountAmt > 0 || payVoucherDiscountAmt > 0) && (
                <div className="small text-muted mt-1">
                  Subtotal: {fmt(payRawTotal)}
                  {payManualDiscountAmt > 0 && (
                    <span className="text-danger ms-2">
                      -{fmt(payManualDiscountAmt)} diskon
                    </span>
                  )}
                  {payVoucherDiscountAmt > 0 && (
                    <span className="text-danger ms-2">
                      -{fmt(payVoucherDiscountAmt)} voucher
                    </span>
                  )}
                </div>
              )}
          </div>

          <div className="pos-payment-methods mb-3">
            {["cash", "debit", "qris", "transfer", "split"].map((m) => (
              <button
                key={m}
                className={`pos-pm-btn ${payMethod === m && !selectedSpecialPayment ? "active" : ""}`}
                onClick={() => {
                  setPayMethod(m);
                  setSelectedSpecialPayment(null);
                }}
              >
                {paymentIcons[m] || <FiDollarSign />}{" "}
                {m === "debit"
                  ? "Debit/Credit"
                  : m === "split"
                    ? "Split"
                    : m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
            {specialPaymentMethods.map((pm) => (
              <button
                key={`spm-${pm.id}`}
                className={`pos-pm-btn ${selectedSpecialPayment?.id === pm.id ? "active" : ""}`}
                onClick={() => {
                  setSelectedSpecialPayment(pm);
                  setPayMethod("special");
                }}
                title={
                  pm.override_price != null
                    ? `Override: ${fmt(pm.override_price)}`
                    : pm.name
                }
              >
                <FiDollarSign /> {pm.name}
              </button>
            ))}
          </div>

          {!selectedSpecialPayment && (
            <details className="pos-payment-extras mb-3">
              <summary>Diskon & Voucher</summary>
              <div className="pt-2">
                <div className="row g-2 mb-2">
                  <div className="col-6">
                    <label className="form-label small">Diskon Manual</label>
                    <div className="input-group input-group-sm">
                      <select
                        className="form-select"
                        style={{ maxWidth: "70px" }}
                        value={payDiscountType}
                        onChange={(e) => setPayDiscountType(e.target.value)}
                      >
                        <option value="flat">Rp</option>
                        <option value="percentage">%</option>
                      </select>
                      <input
                        type="number"
                        className="form-control"
                        value={payDiscount}
                        onChange={(e) => setPayDiscount(e.target.value)}
                        placeholder="0"
                        min="0"
                        max={payDiscountType === "percentage" ? 100 : undefined}
                      />
                    </div>
                    {payDiscount > 0 && (
                      <div className="small text-muted mt-1">
                        ={" "}
                        {fmt(
                          payDiscountType === "percentage"
                            ? Number(payingTx?.total_amount) *
                                (Number(payDiscount) / 100)
                            : Number(payDiscount),
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Kode Voucher</label>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        value={payVoucherCode}
                        onChange={(e) => setPayVoucherCode(e.target.value)}
                        placeholder="Kode"
                      />
                      <button
                        className="btn btn-outline-primary"
                        onClick={validatePayVoucher}
                      >
                        Cek
                      </button>
                    </div>
                    {payValidatedVoucher && (
                      <div className="small text-success mt-1">
                        ✓{" "}
                        {payValidatedVoucher.discount_type === "percentage"
                          ? `${payValidatedVoucher.discount_value}%`
                          : fmt(payValidatedVoucher.discount_value)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </details>
          )}

          {payMethod === "split" && !selectedSpecialPayment && (
            <div className="mb-3">
              {paySplitRows.map((row, idx) => (
                <div key={idx} className="d-flex gap-2 align-items-center mb-2">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "120px", flexShrink: 0 }}
                    value={row.method}
                    onChange={(e) => {
                      const updated = [...paySplitRows];
                      updated[idx] = {
                        ...updated[idx],
                        method: e.target.value,
                      };
                      setPaySplitRows(updated);
                    }}
                  >
                    <option value="cash">Cash</option>
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                    <option value="qris">QRIS</option>
                    <option value="transfer">Transfer</option>
                  </select>
                  <input
                    type="number"
                    className="form-control form-control-sm text-end"
                    placeholder="0"
                    value={row.amount}
                    onChange={(e) => {
                      const updated = [...paySplitRows];
                      updated[idx] = {
                        ...updated[idx],
                        amount: e.target.value,
                      };
                      setPaySplitRows(updated);
                    }}
                  />
                  {paySplitRows.length > 2 && (
                    <button
                      className="btn btn-sm btn-outline-danger px-2"
                      onClick={() =>
                        setPaySplitRows(
                          paySplitRows.filter((_, i) => i !== idx),
                        )
                      }
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                className="btn btn-sm btn-outline-secondary w-100 mb-2"
                onClick={() =>
                  setPaySplitRows([
                    ...paySplitRows,
                    { method: "cash", amount: "" },
                  ])
                }
              >
                + Tambah Metode
              </button>
              {(() => {
                const splitTotal = paySplitRows.reduce(
                  (s, r) => s + Number(r.amount || 0),
                  0,
                );
                const diff = splitTotal - payFinalTotal;
                if (splitTotal === 0) return null;
                return (
                  <div
                    className={`small mt-1 ${Math.abs(diff) <= 1 ? "text-success" : "text-danger"}`}
                  >
                    Total dibayar: <strong>{fmt(splitTotal)}</strong>
                    {Math.abs(diff) > 1
                      ? ` — ${diff > 0 ? "lebih" : "kurang"} ${fmt(Math.abs(diff))}`
                      : " ✓ Sesuai"}
                    {diff > 1 && (
                      <span className="text-warning ms-2">
                        Kembali: {fmt(diff)}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {payMethod === "cash" && !selectedSpecialPayment && (
            <div className="mb-3">
              <label className="form-label small fw-bold">Jumlah Bayar</label>
              <input
                type="number"
                className="form-control form-control-lg"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="0"
                autoFocus
              />
              <div className="d-flex gap-1 mt-2 flex-wrap">
                {(() => {
                  const steps = [
                    1000, 5000, 10000, 20000, 50000, 100000, 200000, 500000,
                    1000000,
                  ];
                  const amounts = [
                    ...new Set(
                      steps.map((s) => Math.ceil(payFinalTotal / s) * s),
                    ),
                  ]
                    .filter((v) => v >= payFinalTotal)
                    .slice(0, 5);
                  return amounts.map((amt) => (
                    <button
                      key={amt}
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setPayAmount(amt.toString())}
                    >
                      {fmt(amt)}
                    </button>
                  ));
                })()}
              </div>
              {Number(payAmount) >= payFinalTotal && (
                <div className="mt-2 p-2 bg-light rounded">
                  <strong>
                    Kembalian: {fmt(Number(payAmount) - payFinalTotal)}
                  </strong>
                </div>
              )}
            </div>
          )}

          <div className="mb-2">
            <label className="form-label small">Catatan</label>
            <textarea
              className="form-control form-control-sm"
              rows="2"
              value={payNotes}
              onChange={(e) => setPayNotes(e.target.value)}
              placeholder="Opsional..."
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPayModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handlePay}>
            {payingTx?.payment_method === "partial" ? "Lunasi" : "Bayar"}{" "}
            {payMethod === "split" && !selectedSpecialPayment
              ? fmt(paySplitRows.reduce((s, r) => s + Number(r.amount || 0), 0))
              : fmt(payFinalTotal)}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DP Modal */}
      <Modal
        show={showDpModal}
        onHide={() => setShowDpModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Catat DP - {dpTx?.invoice_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 text-center fw-bold fs-5">
            Total: {fmt(dpTx?.total_amount)}
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Jumlah DP</label>
            <input
              type="number"
              className="form-control form-control-lg text-center"
              value={dpAmount}
              onChange={(e) => setDpAmount(e.target.value)}
              placeholder="0"
              autoFocus
            />
            {Number(dpAmount) > 0 &&
              Number(dpAmount) < Number(dpTx?.total_amount) && (
                <div className="small text-muted mt-1">
                  Sisa:{" "}
                  <strong>
                    {fmt(Number(dpTx?.total_amount) - Number(dpAmount))}
                  </strong>
                </div>
              )}
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">
              Metode Pembayaran DP
            </label>
            <select
              className="form-select"
              value={dpMethod}
              onChange={(e) => setDpMethod(e.target.value)}
            >
              <option value="cash">Cash</option>
              <option value="debit">Debit</option>
              <option value="qris">QRIS</option>
              <option value="transfer">Transfer</option>
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDpModal(false)}>
            Batal
          </Button>
          <Button variant="warning" onClick={handleDp}>
            Simpan DP {dpAmount ? fmt(Number(dpAmount)) : ""}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size={user?.role === "admin" ? "xl" : "lg"}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FiEdit2 className="me-2" />
            Edit Transaksi - {editingTx?.invoice_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {user?.role === "admin" ? (
            <>
              {/* Admin: tabbed layout */}
              <ul className="nav nav-tabs mb-3">
                {[
                  { key: "umum", label: "Info Umum" },
                  { key: "pembayaran", label: "Pembayaran" },
                  { key: "item", label: "Item Transaksi" },
                ].map((t) => (
                  <li key={t.key} className="nav-item">
                    <button
                      type="button"
                      className={`nav-link${editTab === t.key ? " active" : ""}`}
                      onClick={() => setEditTab(t.key)}
                    >
                      {t.label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Tab: Info Umum */}
              {editTab === "umum" && (
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Tanggal Diterima
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Status</label>
                    <select
                      className="form-select"
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                    >
                      <option value="diterima">Diterima</option>
                      <option value="dicuci">Dicuci</option>
                      <option value="disetrika">Disetrika</option>
                      <option value="selesai_setrika">Selesai Setrika</option>
                      <option value="siap_ambil">Siap Ambil</option>
                      <option value="diambil">Diambil</option>
                      <option value="cancelled">Dibatalkan</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Berat (kg)</label>
                    <input
                      type="number"
                      className="form-control"
                      step="0.1"
                      value={editWeightKg}
                      onChange={(e) => setEditWeightKg(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Jumlah Pcs</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editTotalPcs}
                      onChange={(e) => setEditTotalPcs(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">
                      Catatan Pakaian
                    </label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={editClothingNotes}
                      onChange={(e) => setEditClothingNotes(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">
                      Catatan Internal
                    </label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Tab: Pembayaran */}
              {editTab === "pembayaran" && (
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Metode Pembayaran
                    </label>
                    <select
                      className="form-select"
                      value={editPaymentMethod}
                      onChange={(e) => setEditPaymentMethod(e.target.value)}
                    >
                      <option value="unpaid">Belum Bayar</option>
                      <option value="partial">Partial (DP)</option>
                      <option value="cash">Cash</option>
                      <option value="debit">Debit</option>
                      <option value="credit">Credit</option>
                      <option value="qris">QRIS</option>
                      <option value="transfer">Transfer</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Nominal Bayar
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={editPaymentAmount}
                      onChange={(e) => setEditPaymentAmount(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Diskon</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editDiscountAmount}
                      onChange={(e) => setEditDiscountAmount(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="editHasDpCheck"
                        checked={editHasDp}
                        onChange={(e) => {
                          setEditHasDp(e.target.checked);
                          if (!e.target.checked) {
                            setEditDpAmount("");
                            setEditDpPaymentMethod("cash");
                          }
                        }}
                      />
                      <label
                        className="form-check-label fw-semibold"
                        htmlFor="editHasDpCheck"
                      >
                        Ada Pembayaran DP
                      </label>
                    </div>
                  </div>
                  {editHasDp && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Jumlah DP
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={editDpAmount}
                          onChange={(e) => setEditDpAmount(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Metode Pembayaran DP
                        </label>
                        <select
                          className="form-select"
                          value={editDpPaymentMethod}
                          onChange={(e) =>
                            setEditDpPaymentMethod(e.target.value)
                          }
                        >
                          <option value="cash">Cash</option>
                          <option value="debit">Debit</option>
                          <option value="credit">Credit</option>
                          <option value="qris">QRIS</option>
                          <option value="transfer">Transfer</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      Tanggal Bayar
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={editPaidAt}
                      onChange={(e) => setEditPaidAt(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Shift</label>
                    <select
                      className="form-select"
                      value={editShiftId}
                      onChange={(e) => setEditShiftId(e.target.value)}
                    >
                      <option value="">-- Tidak ada shift --</option>
                      {editShifts.map((s) => (
                        <option key={s.id} value={s.id}>
                          #{s.id} — {s.user_name} — {fmtDt(s.start_time)}
                          {s.status === "open" ? " (Buka)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Tab: Item Transaksi */}
              {editTab === "item" && (
                <div>
                  <div className="mb-3">
                    {editItems.length === 0 ? (
                      <div className="text-muted small">Tidak ada item</div>
                    ) : (
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Item</th>
                            <th style={{ width: "80px" }}>Qty</th>
                            <th style={{ width: "120px" }}>Harga</th>
                            <th style={{ width: "130px" }}>Subtotal</th>
                            <th style={{ width: "40px" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {editItems.map((item, idx) => (
                            <tr
                              key={idx}
                              className={item.isNew ? "table-success" : ""}
                            >
                              <td>
                                {item.name}
                                {item.isNew && (
                                  <span
                                    className="badge bg-success ms-1"
                                    style={{ fontSize: "0.65em" }}
                                  >
                                    Baru
                                  </span>
                                )}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={item.quantity}
                                  min="1"
                                  onChange={(e) => {
                                    const updated = [...editItems];
                                    updated[idx] = {
                                      ...item,
                                      quantity: Number(e.target.value) || 1,
                                    };
                                    setEditItems(updated);
                                  }}
                                />
                              </td>
                              <td>{fmt(item.price)}</td>
                              <td>{fmt(item.price * item.quantity)}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-danger py-0 px-1"
                                  onClick={() =>
                                    setEditItems(
                                      editItems.filter((_, i) => i !== idx),
                                    )
                                  }
                                >
                                  <FiX size={12} />
                                </button>
                              </td>
                            </tr>
                          ))}
                          <tr className="table-light fw-bold">
                            <td colSpan="3" className="text-end">
                              Total
                            </td>
                            <td>
                              {fmt(
                                editItems.reduce(
                                  (s, i) => s + i.price * i.quantity,
                                  0,
                                ),
                              )}
                            </td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                  {/* Add new item */}
                  <div className="mb-2 border rounded p-2 bg-light">
                    <label className="form-label fw-bold mb-2">
                      Tambah Item
                    </label>
                    <div className="row g-2 align-items-end">
                      <div className="col-auto">
                        <label className="form-label small mb-1">Tipe</label>
                        <select
                          className="form-select form-select-sm"
                          value={editItemType}
                          onChange={(e) => {
                            setEditItemType(e.target.value);
                            setEditItemId("");
                          }}
                        >
                          <option value="service">Layanan</option>
                          <option value="product">Produk</option>
                        </select>
                      </div>
                      <div className="col">
                        <label className="form-label small mb-1">
                          {editItemType === "service" ? "Layanan" : "Produk"}
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={editItemId}
                          onChange={(e) => setEditItemId(e.target.value)}
                        >
                          <option value="">-- Pilih --</option>
                          {(editItemType === "service"
                            ? editServices
                            : editProducts
                          ).map((i) => (
                            <option key={i.id} value={i.id}>
                              {i.name} —{" "}
                              {fmt(
                                editItemType === "service"
                                  ? i.price
                                  : i.sell_price,
                              )}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-auto">
                        <label className="form-label small mb-1">Qty</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: "70px" }}
                          value={editItemQty}
                          min="1"
                          onChange={(e) => setEditItemQty(e.target.value)}
                        />
                      </div>
                      <div className="col-auto">
                        <label className="form-label small mb-1 d-block">
                          &nbsp;
                        </label>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={addEditItem}
                        >
                          <FiPlus /> Tambah
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Non-admin: items only (unpaid only) */
            <>
              {/* Date */}
              <div className="mb-3">
                <label className="form-label fw-bold">Tanggal Transaksi</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                />
              </div>

              {/* Unified items list — editable only for unpaid */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  Item Transaksi
                  {editingTx?.payment_method !== "unpaid" && (
                    <span className="text-muted fw-normal small ms-2">
                      (read-only — transaksi sudah dibayar)
                    </span>
                  )}
                </label>
                {editItems.length === 0 ? (
                  <div className="text-muted small">Tidak ada item</div>
                ) : (
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Item</th>
                        <th style={{ width: "80px" }}>Qty</th>
                        <th style={{ width: "120px" }}>Harga</th>
                        <th style={{ width: "130px" }}>Subtotal</th>
                        {editingTx?.payment_method === "unpaid" && (
                          <th style={{ width: "40px" }}></th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {editItems.map((item, idx) => (
                        <tr
                          key={idx}
                          className={item.isNew ? "table-success" : ""}
                        >
                          <td>
                            {item.name}
                            {item.isNew && (
                              <span
                                className="badge bg-success ms-1"
                                style={{ fontSize: "0.65em" }}
                              >
                                Baru
                              </span>
                            )}
                          </td>
                          <td>
                            {editingTx?.payment_method === "unpaid" ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={item.quantity}
                                min="1"
                                onChange={(e) => {
                                  const updated = [...editItems];
                                  updated[idx] = {
                                    ...item,
                                    quantity: Number(e.target.value) || 1,
                                  };
                                  setEditItems(updated);
                                }}
                              />
                            ) : (
                              item.quantity
                            )}
                          </td>
                          <td>{fmt(item.price)}</td>
                          <td>{fmt(item.price * item.quantity)}</td>
                          {editingTx?.payment_method === "unpaid" && (
                            <td>
                              <button
                                className="btn btn-sm btn-outline-danger py-0 px-1"
                                onClick={() =>
                                  setEditItems(
                                    editItems.filter((_, i) => i !== idx),
                                  )
                                }
                              >
                                <FiX size={12} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                      <tr className="table-light fw-bold">
                        <td colSpan="3" className="text-end">
                          Total
                        </td>
                        <td>
                          {fmt(
                            editItems.reduce(
                              (s, i) => s + i.price * i.quantity,
                              0,
                            ),
                          )}
                        </td>
                        {editingTx?.payment_method === "unpaid" && <td></td>}
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              {/* Add new item — only if unpaid */}
              {editingTx?.payment_method === "unpaid" && (
                <div className="mb-2 border rounded p-2 bg-light">
                  <label className="form-label fw-bold mb-2">Tambah Item</label>
                  <div className="row g-2 align-items-end">
                    <div className="col-auto">
                      <label className="form-label small mb-1">Tipe</label>
                      <select
                        className="form-select form-select-sm"
                        value={editItemType}
                        onChange={(e) => {
                          setEditItemType(e.target.value);
                          setEditItemId("");
                        }}
                      >
                        <option value="service">Layanan</option>
                        <option value="product">Produk</option>
                      </select>
                    </div>
                    <div className="col">
                      <label className="form-label small mb-1">
                        {editItemType === "service" ? "Layanan" : "Produk"}
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={editItemId}
                        onChange={(e) => setEditItemId(e.target.value)}
                      >
                        <option value="">-- Pilih --</option>
                        {(editItemType === "service"
                          ? editServices
                          : editProducts
                        ).map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.name} —{" "}
                            {fmt(
                              editItemType === "service"
                                ? i.price
                                : i.sell_price,
                            )}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-auto">
                      <label className="form-label small mb-1">Qty</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: "70px" }}
                        value={editItemQty}
                        min="1"
                        onChange={(e) => setEditItemQty(e.target.value)}
                      />
                    </div>
                    <div className="col-auto">
                      <label className="form-label small mb-1 d-block">
                        &nbsp;
                      </label>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={addEditItem}
                      >
                        <FiPlus /> Tambah
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleEditSave}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* WhatsApp Confirmation Modal */}
      <Modal
        show={showWAConfirm}
        onHide={() => {
          setShowWAConfirm(false);
          setDoneTx(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FiSend className="me-2" />
            Kirim WhatsApp
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Transaksi <strong>{doneTx?.invoice_number}</strong> sudah selesai
            dan siap diambil.
          </p>
          {doneTx?.customer_phone ? (
            <p>
              Kirim notifikasi WhatsApp ke{" "}
              <strong>{doneTx?.customer_name}</strong> ({doneTx?.customer_phone}
              )?
            </p>
          ) : (
            <p className="text-muted">
              Pelanggan tidak memiliki nomor HP yang terdaftar.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowWAConfirm(false);
              setDoneTx(null);
            }}
          >
            Tidak
          </Button>
          <Button
            variant="success"
            onClick={sendWA}
            disabled={waSending || !doneTx?.customer_phone}
          >
            {waSending ? "Mengirim..." : "Kirim WhatsApp"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        confirmLabel={confirmModal.confirmLabel}
        confirmVariant={confirmModal.confirmVariant}
      />

      {/* Cancel (Batal) Transaksi Modal */}
      <Modal
        show={cancelModal.show}
        onHide={() =>
          !cancelModal.loading &&
          setCancelModal({ show: false, tx: null, reason: "", loading: false })
        }
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            <FiTrash2 className="me-2 text-danger" />
            Batalkan Transaksi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning py-2 small mb-3">
            Transaksi <strong>{cancelModal.tx?.invoice_number}</strong> akan
            dibatalkan. Stok produk akan dikembalikan. Transaksi tetap tersimpan
            dengan status <strong>Dibatalkan</strong>.
          </div>
          <div className="mb-2">
            <label className="form-label small fw-semibold">
              Alasan Pembatalan <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control form-control-sm"
              rows={3}
              placeholder="Contoh: Pelanggan membatalkan pesanan, salah input, dll."
              value={cancelModal.reason}
              onChange={(e) =>
                setCancelModal((m) => ({ ...m, reason: e.target.value }))
              }
              disabled={cancelModal.loading}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setCancelModal({
                show: false,
                tx: null,
                reason: "",
                loading: false,
              })
            }
            disabled={cancelModal.loading}
          >
            Tidak
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleCancelConfirm}
            disabled={cancelModal.loading || !cancelModal.reason.trim()}
          >
            {cancelModal.loading ? "Memproses..." : "Ya, Batalkan"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Packing Count Modal */}
      <Modal
        show={showPackingModal}
        onHide={() => {
          setShowPackingModal(false);
          setPackingTx(null);
        }}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {packingTargetStatus === "siap_ambil"
              ? "Siap Diambil"
              : "Selesai Setrika"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3 text-muted small">
            Transaksi <strong>{packingTx?.invoice_number}</strong>
          </p>
          <label className="form-label fw-semibold">Jumlah Packingan</label>
          <div className="input-group">
            <input
              type="number"
              className="form-control"
              min="1"
              step="1"
              value={packingCount}
              onChange={(e) => setPackingCount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitPacking()}
              placeholder="contoh: 3"
              autoFocus
            />
            <span className="input-group-text">pack</span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowPackingModal(false);
              setPackingTx(null);
            }}
          >
            Batal
          </Button>
          <Button
            variant={packingTargetStatus === "siap_ambil" ? "success" : "dark"}
            size="sm"
            onClick={submitPacking}
          >
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* WA Invoice Confirmation after payment */}
      <Modal
        show={showWAInvoiceConfirm}
        onHide={() => {
          setShowWAInvoiceConfirm(false);
          setPaidTxForWA(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FiSend className="me-2" />
            Kirim Invoice WhatsApp
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Pembayaran transaksi <strong>{paidTxForWA?.invoice_number}</strong>{" "}
            berhasil.
          </p>
          <p>
            Kirim invoice WhatsApp ke{" "}
            <strong>{paidTxForWA?.customer_name}</strong> (
            {paidTxForWA?.customer_phone})?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowWAInvoiceConfirm(false);
              setPaidTxForWA(null);
            }}
          >
            Tidak
          </Button>
          <Button
            variant="success"
            disabled={waInvoiceSending}
            onClick={async () => {
              setWaInvoiceSending(true);
              try {
                const preview = await api.get(
                  `/transactions/${paidTxForWA.id}/whatsapp-preview`,
                );
                let phone = (preview.data.phone || "").replace(/[^0-9]/g, "");
                if (phone.startsWith("0")) phone = "62" + phone.slice(1);
                else if (phone.startsWith("8")) phone = "62" + phone;
                const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(preview.data.message || "")}`;
                window.open(waUrl, "_blank");
                toast.success("WhatsApp Web dibuka!");
              } catch {
                toast.error("Gagal membuka WhatsApp Web");
              } finally {
                setWaInvoiceSending(false);
                setShowWAInvoiceConfirm(false);
                setPaidTxForWA(null);
              }
            }}
          >
            {waInvoiceSending ? "Membuka..." : "Kirim WhatsApp"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Transactions;
